import { alertsApi } from './alerts.api';

/**
 * normalize Alerts takes an alert object and ensures that certain fields have consistent data types
 * (e.g. workplaceId as number, is_internal_use as boolean) to prevent type-related bugs in the frontend when processing alerts from various sources
 * (API responses, SSE events, etc.)
 *
 * @param {import('../../features/Weather/features/WeatherAlerts/types/alerts.types').Alert} alert - the alert object to normalize
 * @returns {import('../../features/Weather/features/WeatherAlerts/types/alerts.types').Alert} normalized alert with consistent data types (e.g. workplaceId as number, is_internal_use as boolean)
 */
const normalizeAlert = (alert) => {
  if (!alert) return alert;
  try {
    const normalized = { ...alert };
    const wpId = Number(alert?.workplaceId);
    if (!isNaN(wpId)) normalized.workplaceId = wpId;

    if ('is_internal_use' in alert) normalized.is_internal_use = Boolean(alert?.is_internal_use);
    return normalized;
  } catch (error) {
    console.error('Error normalizing alert:', error, 'Original alert:', alert);
  }
};

/**
 * The identity of an active-alert row.
 *
 * `GET /v2/alert` rows and the `alert-add` / `alert-update` payloads are keyed by
 * `message_id`; no row carries `id`. Matching on `id` alone made
 * `undefined === undefined` true, so every incoming alert merged into row 0 and
 * overwrote the first alert on screen. `id` stays in the chain for the
 * `alert-remove` payload, which still sends one.
 *
 * @param {Object} alert - An alert row or event payload.
 * @returns {string|number|undefined} The key, or `undefined` when there is none.
 */
const alertKey = (alert) => alert?.message_id ?? alert?.id;

/**
 * Compares two alerts by key.
 *
 * FEMA and Custom Weather rows carry string ids and `Custom_Alerts` rows carry
 * integers, so the comparison is on the string form. An alert with no key never
 * matches anything — that is the `undefined === undefined` defect.
 *
 * @param {Object} alert - A cached alert row.
 * @param {Object} other - The incoming alert.
 * @returns {boolean} `true` when both carry the same key.
 */
const isSameAlert = (alert, other) => {
  const key = alertKey(alert);
  const otherKey = alertKey(other);
  if (key == null || otherKey == null) return false;
  return String(key) === String(otherKey);
};

/**
 * Normalizes an SSE alert payload into an array of usable alerts.
 *
 * The backend type is `Alert | Alert[]`; `normalizeAlert` returns `undefined`
 * for anything it cannot copy.
 *
 * @param {Object|Object[]} eventData - One alert or many.
 * @returns {Object[]} The normalized alerts.
 */
const toNormalizedAlerts = (eventData) =>
  (Array.isArray(eventData) ? eventData : [eventData]).map(normalizeAlert).filter(Boolean);

// Apply one or more alert updates (from server events) into the RTK Query cache
export const applyAlertUpdatesFromEvent = (dispatch, updates) => {
  const updatesArray = updates ? toNormalizedAlerts(updates) : [];

  if (!updatesArray.length) return;

  dispatch(
    alertsApi.util.updateQueryData(
      alertsApi.endpoints.getAllActiveAlerts.name,
      undefined,
      (draft) => {
        if (!Array.isArray(draft) || !draft.length) return;

        updatesArray.forEach((update) => {
          const indexOfAlertToUpdate = draft.findIndex((a) => isSameAlert(a, update));

          if (indexOfAlertToUpdate < 0) return; // not found

          const prev = draft[indexOfAlertToUpdate];
          draft[indexOfAlertToUpdate] = {
            ...prev,
            ...update,
          };
        });
      },
    ),
  );
};

// Add alert from event (or replace if exists)
export const addAlertFromEvent = (dispatch, eventData) => {
  if (!eventData) return;
  const normalizedAlerts = toNormalizedAlerts(eventData);
  if (!normalizedAlerts.length) return;

  dispatch(
    alertsApi.util.updateQueryData(
      alertsApi.endpoints.getAllActiveAlerts.name,
      undefined,
      (draft) => {
        if (!Array.isArray(draft)) return;

        normalizedAlerts.forEach((normalizedAlert) => {
          const existingIndex = draft.findIndex((a) => isSameAlert(a, normalizedAlert));
          if (existingIndex >= 0) {
            draft[existingIndex] = {
              ...draft[existingIndex],
              ...normalizedAlert,
            };
          } else {
            draft.push(normalizedAlert);
          }
        });
      },
    ),
  );
};

// Remove alert from cache by key (`message_id`, or `id` when that is all the
// payload carries), one or many
export const removeAlertFromEvent = (dispatch, eventData) => {
  if (!eventData) return;

  const keys = (Array.isArray(eventData) ? eventData : [eventData])
    .map(alertKey)
    .filter((key) => key != null && key !== '')
    .map(String);
  if (!keys.length) return;

  dispatch(
    alertsApi.util.updateQueryData(
      alertsApi.endpoints.getAllActiveAlerts.name,
      undefined,
      (draft) => {
        if (!Array.isArray(draft)) return;

        const kept = draft.filter((a) => {
          const key = alertKey(a);
          return key == null || !keys.includes(String(key));
        });
        if (kept.length !== draft.length) draft.splice(0, draft.length, ...kept);
      },
    ),
  );
};

// Export default helpers if needed
export default {
  applyAlertUpdatesFromEvent,
  addAlertFromEvent,
  removeAlertFromEvent,
};

// DEPRECATED — getAlertNotifierEventSource removed.
// Alert SSE events are now received through AppSSEProvider / useAppSSE()
// in src/contexts/AppSSEContext.jsx.
